import confetti from "canvas-confetti";

// A single shared AudioContext — browsers cap concurrent contexts (~6/tab), so
// creating a new one per celebration eventually throws and kills the sounds.
let _audioCtx = null;
function getAudioCtx() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!_audioCtx || _audioCtx.state === "closed") _audioCtx = new Ctx();
  if (_audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
  return _audioCtx;
}

export function fireConfetti() {
  const colors = ["#CCFF00", "#00E5FF", "#FFD600", "#FF5100"];
  confetti({ particleCount: 80, spread: 70, origin: { y: 0.7 }, colors });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors }), 150);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors }), 300);
}

// Big, triumphant "wahoo!" — louder and longer than a simple chime.
export function playCelebration() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const notes = [
      { f: 523.25, t: 0.00, d: 0.18 }, // C5
      { f: 659.25, t: 0.13, d: 0.18 }, // E5
      { f: 783.99, t: 0.26, d: 0.18 }, // G5
      { f: 1046.5, t: 0.39, d: 0.20 }, // C6
      { f: 1318.5, t: 0.52, d: 0.48 }, // E6 (bright held top)
    ];
    notes.forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(f, t0 + t);
      gain.gain.setValueAtTime(0.0001, t0 + t);
      gain.gain.exponentialRampToValueAtTime(0.5, t0 + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + t + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + t);
      osc.stop(t0 + t + d + 0.05);
    });
    // Long, rising "whoooop!" tail.
    const whoop = ctx.createOscillator();
    const wg = ctx.createGain();
    whoop.type = "sine";
    whoop.frequency.setValueAtTime(500, t0 + 0.6);
    whoop.frequency.exponentialRampToValueAtTime(1500, t0 + 1.15);
    wg.gain.setValueAtTime(0.0001, t0 + 0.6);
    wg.gain.exponentialRampToValueAtTime(0.45, t0 + 0.68);
    wg.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.25);
    whoop.connect(wg).connect(ctx.destination);
    whoop.start(t0 + 0.6);
    whoop.stop(t0 + 1.3);
  } catch { /* audio not available — silent */ }
}

// A cheeky, buzzy KAZOO fanfare for finishing a video — louder and longer.
export function playVideoCheer() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    // Rising "doo-doo" lead-in with a buzzy sawtooth = kazoo timbre.
    const notes = [
      { f: 392.00, t: 0.00, d: 0.20 }, // G4
      { f: 523.25, t: 0.18, d: 0.20 }, // C5
    ];
    notes.forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(f, t0 + t);
      gain.gain.setValueAtTime(0.0001, t0 + t);
      gain.gain.exponentialRampToValueAtTime(0.4, t0 + t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + t + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + t);
      osc.stop(t0 + t + d + 0.05);
    });
    // Held final note with vibrato — the classic kazoo "buzzzz".
    const held = ctx.createOscillator();
    const hg = ctx.createGain();
    held.type = "sawtooth";
    held.frequency.setValueAtTime(659.25, t0 + 0.36); // E5
    const vib = ctx.createOscillator();
    const vibGain = ctx.createGain();
    vib.frequency.setValueAtTime(12, t0 + 0.36);  // ~12Hz wobble
    vibGain.gain.setValueAtTime(20, t0 + 0.36);    // +/-20Hz depth
    vib.connect(vibGain).connect(held.frequency);
    hg.gain.setValueAtTime(0.0001, t0 + 0.36);
    hg.gain.exponentialRampToValueAtTime(0.42, t0 + 0.4);
    hg.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.0);
    held.connect(hg).connect(ctx.destination);
    vib.start(t0 + 0.36); held.start(t0 + 0.36);
    vib.stop(t0 + 1.05); held.stop(t0 + 1.05);
  } catch { /* audio not available — silent */ }
}

const FW_COLORS = ["#CCFF00", "#00E5FF", "#FFD600", "#FF5100", "#FF3DB5"];

// --- Low-frequency "boom" via filtered noise (used for firework bursts + explosion) ---
function boom(ctx, at, vol = 0.5, dur = 0.5) {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
  const src = ctx.createBufferSource(); src.buffer = buf;
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, at);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.setValueAtTime(1000, at);
  src.connect(lp).connect(g).connect(ctx.destination);
  src.start(at); src.stop(at + dur);
}

// A light firework "pop" — short bright crackle, quieter than the quiz explosion.
function playPop() {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    const dur = 0.22;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 3);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass";
    bp.frequency.setValueAtTime(1400 + Math.random() * 500, t0); bp.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.3, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(t0); src.stop(t0 + dur);
  } catch { /* silent */ }
}

function fireworkBurst() {
  const origin = { x: 0.2 + Math.random() * 0.6, y: 0.28 + Math.random() * 0.22 };
  confetti({ particleCount: 90, startVelocity: 34, spread: 360, ticks: 110, gravity: 1.1, scalar: 1, origin, colors: FW_COLORS });
  setTimeout(() => confetti({ particleCount: 55, startVelocity: 26, spread: 360, ticks: 95, gravity: 1.1, scalar: 0.8, origin: { x: 0.2 + Math.random() * 0.6, y: 0.28 + Math.random() * 0.22 }, colors: FW_COLORS }), 130);
}

// Three rapid-fire firework bursts (~30% overlap), each with a light "pop".
export function fireFireworks() {
  const set = () => { playPop(); fireworkBurst(); };
  for (let i = 0; i < 3; i++) setTimeout(set, i * 380);
}

// Shoot coins UP from the bottom-center; gravity brings them back down.
// Hand-drawn solid 3D coin: gold face with a radial sheen, visible edge thickness,
// thin black outline, inner rim + "$" emboss. Drawn once at 3x and cached.
let _coinShapeCache;
function coinShape() {
  if (_coinShapeCache !== undefined) return _coinShapeCache;
  try {
    if (typeof OffscreenCanvas === "undefined") { _coinShapeCache = null; return null; }
    const res = 13.2;                       // 3x the display scalar (4.4) for crisp edges
    const w = Math.ceil(10 * res);
    const out = Math.max(2, w * 0.028);     // black outline width
    const rx = w / 2 - out;
    const ry = rx * 0.8;
    const thick = w * 0.17;                 // edge thickness
    const h = Math.ceil(out * 2 + ry * 2 + thick);
    const cx = w / 2;
    const topY = out + ry;
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    ctx.lineJoin = "round";
    // Edge (side band) — darker gold
    ctx.fillStyle = "#C7940A";
    ctx.beginPath(); ctx.ellipse(cx, topY + thick, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.rect(cx - rx, topY, rx * 2, thick); ctx.fill();
    // Black outline on the visible silhouette of the edge
    ctx.strokeStyle = "#111111"; ctx.lineWidth = out;
    ctx.beginPath();
    ctx.ellipse(cx, topY + thick, rx, ry, 0, 0, Math.PI);
    ctx.moveTo(cx - rx, topY + thick); ctx.lineTo(cx - rx, topY);
    ctx.moveTo(cx + rx, topY + thick); ctx.lineTo(cx + rx, topY);
    ctx.stroke();
    // Face — solid gold with a soft highlight
    const grad = ctx.createRadialGradient(cx - rx * 0.35, topY - ry * 0.4, rx * 0.1, cx, topY, rx);
    grad.addColorStop(0, "#FFE566"); grad.addColorStop(0.65, "#FFC400"); grad.addColorStop(1, "#EFA400");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.ellipse(cx, topY, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(cx, topY, rx, ry, 0, 0, Math.PI * 2); ctx.stroke();
    // Inner rim
    ctx.strokeStyle = "#B8860B"; ctx.lineWidth = out * 0.7;
    ctx.beginPath(); ctx.ellipse(cx, topY, rx * 0.72, ry * 0.72, 0, 0, Math.PI * 2); ctx.stroke();
    // "$" emboss
    ctx.fillStyle = "#A9780A";
    ctx.font = `900 ${Math.round(rx * 0.95)}px Arial, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("$", cx, topY + out * 0.2);
    const scale = 1 / res;
    _coinShapeCache = {
      type: "bitmap",
      bitmap: canvas.transferToImageBitmap(),
      matrix: [scale, 0, 0, scale, -w * scale / 2, -h * scale / 2],
    };
  } catch { _coinShapeCache = null; }
  return _coinShapeCache;
}

export function shootCoins() {
  let shapes;
  const coin = coinShape();
  if (coin) shapes = [coin];
  else { try { if (confetti.shapeFromText) shapes = [confetti.shapeFromText({ text: "🪙", scalar: 4.4 })]; } catch { /* no emoji shapes */ } }
  const opts = { particleCount: 45, startVelocity: 58, spread: 60, angle: 90, gravity: 1.15, scalar: 4.4, ticks: 220, origin: { x: 0.5, y: 0.95 } };
  if (shapes) opts.shapes = shapes; else opts.colors = ["#FFD600", "#FF9E00", "#FFC400"];
  confetti(opts);
}

// Rapid metallic "cling-cling" — slot-machine coins striking each other.
export function playCoins(count = 10) {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    for (let i = 0; i < count; i++) {
      const t = t0 + i * 0.055;
      const f = 1300 + Math.random() * 700;
      [[f, 0.15], [f * 1.5, 0.08]].forEach(([freq, vol]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "square"; o.frequency.setValueAtTime(freq, t);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(vol, t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
        o.connect(g).connect(ctx.destination);
        o.start(t); o.stop(t + 0.1);
      });
    }
  } catch { /* silent */ }
}

// Crystal "ching" for the diamond forge — bell-like inharmonic partials with a long
// glassy shimmer, followed by a tiny rising sparkle arpeggio.
export function playDiamondChing() {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    // The strike: glass bell partials (inharmonic ratios keep it "crystal", not "coin").
    [[2093, 0.22, 1.9], [2093 * 2.76, 0.1, 1.4], [2093 * 5.4, 0.05, 0.9]].forEach(([f, vol, dur]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(vol, t0 + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      o.connect(g).connect(ctx.destination);
      o.start(t0); o.stop(t0 + dur + 0.05);
    });
    // Rising sparkle arpeggio as the shine settles.
    [2637, 3136, 3951, 5274].forEach((f, i) => {
      const t = t0 + 0.28 + i * 0.09;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "triangle"; o.frequency.setValueAtTime(f, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.07, t + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(t); o.stop(t + 0.4);
    });
  } catch { /* silent */ }
}

// A fun exploding "boom" for the grand-total pop.
export function playExplosion() {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    boom(ctx, t0, 0.7, 0.7);
    // little upward zip before the boom
    const z = ctx.createOscillator(); const zg = ctx.createGain();
    z.type = "sawtooth";
    z.frequency.setValueAtTime(200, t0); z.frequency.exponentialRampToValueAtTime(900, t0 + 0.12);
    zg.gain.setValueAtTime(0.0001, t0); zg.gain.exponentialRampToValueAtTime(0.2, t0 + 0.03); zg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    z.connect(zg).connect(ctx.destination); z.start(t0); z.stop(t0 + 0.16);
  } catch { /* silent */ }
}

// Heavy mallet THUD on the strike pad.
export function playThud() {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    boom(ctx, t0, 0.75, 0.4);
    const k = ctx.createOscillator(); const kg = ctx.createGain();
    k.type = "square";
    k.frequency.setValueAtTime(120, t0); k.frequency.exponentialRampToValueAtTime(45, t0 + 0.18);
    kg.gain.setValueAtTime(0.5, t0); kg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
    k.connect(kg).connect(ctx.destination); k.start(t0); k.stop(t0 + 0.25);
  } catch { /* silent */ }
}

// Big carnival bell DING! — bright strike with a long ringing tail.
// times > 1 keeps it clanging (ding-ding-ding) for as long as the coins fly.
export function playBellDing(times = 1, gap = 0.5) {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const start = ctx.currentTime;
    for (let n = 0; n < times; n++) {
      const t0 = start + n * gap;
      const fade = Math.pow(0.88, n); // each clang slightly softer
      [[1567.98, 0.5, 1.4], [2349.3, 0.28, 1.0], [3135.96, 0.14, 0.7]].forEach(([f, vol, dur]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.setValueAtTime(f, t0);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(vol * fade, t0 + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
        o.connect(g).connect(ctx.destination); o.start(t0); o.stop(t0 + dur + 0.05);
      });
      const strike = ctx.createOscillator(); const sg = ctx.createGain();
      strike.type = "square"; strike.frequency.setValueAtTime(4200, t0);
      sg.gain.setValueAtTime(0.12 * fade, t0); sg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
      strike.connect(sg).connect(ctx.destination); strike.start(t0); strike.stop(t0 + 0.06);
    }
  } catch { /* silent */ }
}

// A happy little walking-away whistle tune (pure sine + vibrato = human whistle).
export function playWhistle() {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    const notes = [
      { f: 783.99, t: 0.00, d: 0.22 }, // G5
      { f: 987.77, t: 0.24, d: 0.22 }, // B5
      { f: 880.00, t: 0.48, d: 0.20 }, // A5
      { f: 783.99, t: 0.70, d: 0.20 }, // G5
      { f: 1174.7, t: 0.94, d: 0.34 }, // D6
      { f: 987.77, t: 1.32, d: 0.42 }, // B5 (held)
    ];
    notes.forEach(({ f, t, d }) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f * 0.97, t0 + t);
      o.frequency.exponentialRampToValueAtTime(f, t0 + t + 0.04);
      const vib = ctx.createOscillator(); const vg = ctx.createGain();
      vib.frequency.setValueAtTime(6, t0 + t); vg.gain.setValueAtTime(8, t0 + t);
      vib.connect(vg).connect(o.frequency);
      g.gain.setValueAtTime(0.0001, t0 + t);
      g.gain.exponentialRampToValueAtTime(0.3, t0 + t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + t + d);
      o.connect(g).connect(ctx.destination);
      vib.start(t0 + t); o.start(t0 + t);
      vib.stop(t0 + t + d + 0.05); o.stop(t0 + t + d + 0.05);
    });
  } catch { /* silent */ }
}

// Coins erupting from an arbitrary screen point (used by the punk rocker's guitar).
export function shootCoinsFrom(x, y) {
  let shapes;
  const coin = coinShape();
  if (coin) shapes = [coin];
  else { try { if (confetti.shapeFromText) shapes = [confetti.shapeFromText({ text: "🪙", scalar: 3.6 })]; } catch { /* no emoji shapes */ } }
  const opts = { particleCount: 16, startVelocity: 52, spread: 75, angle: 90, gravity: 1.15, scalar: 3.6, ticks: 200, origin: { x, y } };
  if (shapes) opts.shapes = shapes; else opts.colors = ["#FFD600", "#FF9E00", "#FFC400"];
  confetti(opts);
}

// Angry distorted power-chord riff for the punk rocker (pure WebAudio, ~3s).
export function playRockRiff(duration = 3) {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) { const v = (i / 128) - 1; curve[i] = Math.tanh(v * 6); }
    shaper.curve = curve;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 2200;
    const master = ctx.createGain(); master.gain.value = 0.16;
    shaper.connect(lp).connect(master).connect(ctx.destination);
    const chug = (t, root, len = 0.18) => {
      [root, root * 1.5, root * 2].forEach((f) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sawtooth"; o.frequency.setValueAtTime(f, t);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.5, t + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t + len);
        o.connect(g).connect(shaper);
        o.start(t); o.stop(t + len + 0.05);
      });
    };
    const E = 82.41, G = 98.0, A = 110.0;
    const bar = [[0, E], [0.2, E], [0.4, E], [0.6, G], [0.8, G], [1.0, A], [1.2, A], [1.35, G], [1.5, E]];
    let off = 0;
    while (off < duration) {
      bar.forEach(([dt, f]) => { if (off + dt < duration) chug(t0 + off + dt, f, dt === 1.5 ? 0.45 : 0.18); });
      off += 1.7;
    }
  } catch { /* silent */ }
}
